import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {GET} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js'
import {Bar, Line, Pie, Doughnut} from 'react-chartjs-2'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
)

type DashboardMetrics = {
  totalAllocations: number
  totalDeallocations: number
  activeAllocations: number
  averageOccupationTime: number
  mostUsedTables: Array<{tableName: string; count: number}>
  leastUsedTables: Array<{tableName: string; count: number}>
  allocationsByEnvironment: Array<{environment: string; count: number}>
  allocationsByHour: Array<{hour: string; count: number}>
  allocationsByDay: Array<{date: string; count: number}>
  allocationsByStatus: Array<{status: string; count: number}>
  occupationRate: number
  averagePeoplePerTable: number
  peakHours: Array<{hour: string; count: number}>
}

const TableManagementDashboardContent: FC = () => {
  const [loading, setLoading] = useState(false)
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)
  const [period, setPeriod] = useState<'today' | 'week' | 'month' | 'year'>('week')

  useEffect(() => {
    loadDashboardData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period])

  const loadDashboardData = async () => {
    try {
      setLoading(true)

      const res = await GET(`/queueReservation/tableAllocation/dashboard?period=${period}`)

      if (res.success && res.data) {
        setMetrics(res.data)
      }

      setLoading(false)
    } catch (e) {
      console.error('Erro ao carregar dashboard:', e)
      setLoading(false)
    }
  }

  const formatTime = (minutes: number) => {
    if (minutes < 60) return `${Math.round(minutes)} min`
    const hours = Math.floor(minutes / 60)
    const mins = Math.round(minutes % 60)
    return `${hours}h ${mins}min`
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>Dashboard - Gestão de Mesas</span>
                <BreadCrumbs />
              </h3>
              <div className='card-toolbar'>
                <select
                  className='form-select form-select-sm'
                  value={period}
                  onChange={(e) => setPeriod(e.target.value as any)}
                >
                  <option value='today'>Hoje</option>
                  <option value='week'>Última Semana</option>
                  <option value='month'>Último Mês</option>
                  <option value='year'>Último Ano</option>
                </select>
              </div>
            </div>

            <div className='card-body'>
              {metrics ? (
                <>
                  {/* Cards de Métricas Principais */}
                  <div className='row g-5 mb-8'>
                    <div className='col-md-3'>
                      <div className='card bg-light-primary'>
                        <div className='card-body'>
                          <div className='d-flex align-items-center'>
                            <i className='fas fa-chair fs-2x text-primary me-4'></i>
                            <div>
                              <h3 className='fw-bolder text-primary mb-1'>
                                {metrics.totalAllocations}
                              </h3>
                              <span className='text-muted fw-bold fs-7'>Total de Alocações</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='col-md-3'>
                      <div className='card bg-light-success'>
                        <div className='card-body'>
                          <div className='d-flex align-items-center'>
                            <i className='fas fa-check-circle fs-2x text-success me-4'></i>
                            <div>
                              <h3 className='fw-bolder text-success mb-1'>
                                {metrics.activeAllocations}
                              </h3>
                              <span className='text-muted fw-bold fs-7'>Mesas Ativas</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='col-md-3'>
                      <div className='card bg-light-warning'>
                        <div className='card-body'>
                          <div className='d-flex align-items-center'>
                            <i className='fas fa-percentage fs-2x text-warning me-4'></i>
                            <div>
                              <h3 className='fw-bolder text-warning mb-1'>
                                {metrics.occupationRate.toFixed(1)}%
                              </h3>
                              <span className='text-muted fw-bold fs-7'>Taxa de Ocupação</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='col-md-3'>
                      <div className='card bg-light-info'>
                        <div className='card-body'>
                          <div className='d-flex align-items-center'>
                            <i className='fas fa-clock fs-2x text-info me-4'></i>
                            <div>
                              <h3 className='fw-bolder text-info mb-1'>
                                {formatTime(metrics.averageOccupationTime)}
                              </h3>
                              <span className='text-muted fw-bold fs-7'>Tempo Médio</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Gráficos - Linha 1: Alocações por Dia e Horário */}
                  <div className='row g-5 mb-5'>
                    <div className='col-lg-6'>
                      <div className='card card-flush h-lg-100'>
                        <div className='card-header pt-5'>
                          <h3 className='card-title align-items-start flex-column'>
                            <span className='card-label fw-bold text-dark'>Alocações por Dia</span>
                            <span className='text-muted mt-1 fw-semibold fs-7'>
                              Evolução diária das alocações
                            </span>
                          </h3>
                        </div>
                        <div className='card-body pt-6'>
                          <Line
                            data={{
                              labels: metrics.allocationsByDay.map((d) => d.date),
                              datasets: [
                                {
                                  label: 'Alocações',
                                  data: metrics.allocationsByDay.map((d) => d.count),
                                  borderColor: 'rgb(75, 192, 192)',
                                  backgroundColor: 'rgba(75, 192, 192, 0.2)',
                                  tension: 0.4,
                                  fill: true,
                                },
                              ],
                            }}
                            options={{
                              responsive: true,
                              maintainAspectRatio: true,
                              plugins: {
                                legend: {
                                  display: false,
                                },
                              },
                              scales: {
                                y: {
                                  beginAtZero: true,
                                },
                              },
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className='col-lg-6'>
                      <div className='card card-flush h-lg-100'>
                        <div className='card-header pt-5'>
                          <h3 className='card-title align-items-start flex-column'>
                            <span className='card-label fw-bold text-dark'>
                              Alocações por Horário
                            </span>
                            <span className='text-muted mt-1 fw-semibold fs-7'>
                              Distribuição ao longo do dia
                            </span>
                          </h3>
                        </div>
                        <div className='card-body pt-6'>
                          <Bar
                            data={{
                              labels: metrics.allocationsByHour.map((h) => h.hour),
                              datasets: [
                                {
                                  label: 'Alocações',
                                  data: metrics.allocationsByHour.map((h) => h.count),
                                  backgroundColor: 'rgba(54, 162, 235, 0.7)',
                                  borderColor: 'rgba(54, 162, 235, 1)',
                                  borderWidth: 2,
                                  borderRadius: 4,
                                },
                              ],
                            }}
                            options={{
                              responsive: true,
                              maintainAspectRatio: true,
                              plugins: {
                                legend: {
                                  display: false,
                                },
                              },
                              scales: {
                                y: {
                                  beginAtZero: true,
                                },
                              },
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Gráficos - Linha 2: Distribuições em Cards */}
                  <div className='row g-5 mb-5'>
                    <div className='col-lg-4'>
                      <div className='card card-flush h-lg-100'>
                        <div className='card-header pt-5'>
                          <h3 className='card-title align-items-start flex-column'>
                            <span className='card-label fw-bold text-dark'>
                              Distribuição por Ambiente
                            </span>
                            <span className='text-muted mt-1 fw-semibold fs-7'>
                              Ocupação por área
                            </span>
                          </h3>
                        </div>
                        <div className='card-body d-flex align-items-center pt-3'>
                          <div className='w-100'>
                            <Pie
                              data={{
                                labels: metrics.allocationsByEnvironment.map((e) => e.environment),
                                datasets: [
                                  {
                                    label: 'Alocações',
                                    data: metrics.allocationsByEnvironment.map((e) => e.count),
                                    backgroundColor: [
                                      'rgba(255, 99, 132, 0.8)',
                                      'rgba(54, 162, 235, 0.8)',
                                      'rgba(255, 206, 86, 0.8)',
                                      'rgba(75, 192, 192, 0.8)',
                                      'rgba(153, 102, 255, 0.8)',
                                      'rgba(255, 159, 64, 0.8)',
                                    ],
                                    borderWidth: 2,
                                    borderColor: '#fff',
                                  },
                                ],
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: true,
                                plugins: {
                                  legend: {
                                    position: 'bottom' as const,
                                    labels: {
                                      padding: 10,
                                      font: {
                                        size: 11,
                                      },
                                    },
                                  },
                                },
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='col-lg-4'>
                      <div className='card card-flush h-lg-100'>
                        <div className='card-header pt-5'>
                          <h3 className='card-title align-items-start flex-column'>
                            <span className='card-label fw-bold text-dark'>
                              Distribuição por Status
                            </span>
                            <span className='text-muted mt-1 fw-semibold fs-7'>
                              Estado das alocações
                            </span>
                          </h3>
                        </div>
                        <div className='card-body d-flex align-items-center pt-3'>
                          <div className='w-100'>
                            <Doughnut
                              data={{
                                labels: metrics.allocationsByStatus.map((s) => s.status),
                                datasets: [
                                  {
                                    label: 'Quantidade',
                                    data: metrics.allocationsByStatus.map((s) => s.count),
                                    backgroundColor: [
                                      'rgba(255, 206, 86, 0.8)',
                                      'rgba(54, 162, 235, 0.8)',
                                      'rgba(75, 192, 192, 0.8)',
                                      'rgba(201, 203, 207, 0.8)',
                                    ],
                                    borderWidth: 2,
                                    borderColor: '#fff',
                                  },
                                ],
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: true,
                                plugins: {
                                  legend: {
                                    position: 'bottom' as const,
                                    labels: {
                                      padding: 10,
                                      font: {
                                        size: 11,
                                      },
                                    },
                                  },
                                },
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='col-lg-4'>
                      <div className='card card-flush h-lg-100'>
                        <div className='card-header pt-5'>
                          <h3 className='card-title align-items-start flex-column'>
                            <span className='card-label fw-bold text-dark'>
                              Mesas Mais Utilizadas
                            </span>
                            <span className='text-muted mt-1 fw-semibold fs-7'>Top 5 mesas</span>
                          </h3>
                        </div>
                        <div className='card-body pt-5'>
                          {metrics.mostUsedTables.slice(0, 5).map((table, idx) => (
                            <div
                              key={idx}
                              className='d-flex align-items-center justify-content-between mb-4'
                            >
                              <div className='d-flex align-items-center'>
                                <div className='symbol symbol-40px me-3'>
                                  <div className='symbol-label bg-light-primary'>
                                    <i className='fas fa-chair text-primary fs-3'></i>
                                  </div>
                                </div>
                                <div>
                                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                  <a href='#' className='text-gray-800 text-hover-primary fw-bold'>
                                    Mesa {table.tableName}
                                  </a>
                                  <span className='text-muted fw-semibold d-block fs-7'>
                                    #{idx + 1}
                                  </span>
                                </div>
                              </div>
                              <div className='text-end'>
                                <span className='badge badge-light-primary fw-bold fs-6'>
                                  {table.count}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Linha 3: Horários de Pico */}
                  <div className='row g-5'>
                    <div className='col-12'>
                      <div className='card card-flush'>
                        <div className='card-header pt-5'>
                          <h3 className='card-title align-items-start flex-column'>
                            <span className='card-label fw-bold text-dark'>Horários de Pico</span>
                            <span className='text-muted mt-1 fw-semibold fs-7'>
                              Períodos de maior movimentação
                            </span>
                          </h3>
                        </div>
                        <div className='card-body pt-5'>
                          <div className='row g-3'>
                            {metrics.peakHours.map((peak, idx) => (
                              <div key={idx} className='col-lg-2 col-md-4 col-6'>
                                <div className='d-flex flex-column bg-light-warning rounded p-4 h-100'>
                                  <div className='d-flex align-items-center mb-2'>
                                    <i className='fas fa-fire text-warning fs-2x me-2'></i>
                                    <span className='fw-bold fs-3 text-warning'>{peak.count}</span>
                                  </div>
                                  <span className='text-gray-700 fw-bold fs-6'>{peak.hour}</span>
                                  <span className='text-muted fs-7 mt-1'>alocações</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className='alert alert-info'>
                  <i className='fas fa-info-circle me-2'></i>
                  Carregando métricas do dashboard...
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const TableManagementDashboard: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: true},
          {
            title: 'Dashboard Mesas',
            path: 'booking/table-management-dashboard',
            isActive: true,
          },
        ]}
      >
        Dashboard - Gestão de Mesas
      </PageTitle>
      <TableManagementDashboardContent />
    </>
  )
}

export {TableManagementDashboard}
